import { useLanguage } from '../../context/LanguageContext';
import EmptyWorkspaceMatrix from '../EmptyWorkspaceMatrix';
import InternalWorkspace from '../InternalWorkspace';
import WorkspaceImageViewer from '../WorkspaceImageViewer';
import WorkspaceErrorBoundary from '../WorkspaceErrorBoundary';
import { ReactPreview } from '../DamyxCodeWorkspace';
import { UnifiedCodePreview } from '../UnifiedCodePreview';

/**
 * WorkspaceMainContent — extraído de Dashboard.jsx en P3.1 sesión 2/4 (feb-2026).
 *
 * Contiene el switch principal del Área de Trabajo:
 *   1. WorkspaceImageViewer (si hay imagen activa + editor abierto).
 *   2. InternalWorkspace (si forceInternalWorkspace).
 *   3. EmptyWorkspaceMatrix (si no hay conversación o contenido).
 *   4. ReactPreview (código ejecutable).
 *   5. UnifiedCodePreview (código previsualizable).
 *   6. Imagen estática (si lastImage).
 *
 * Reglas del cuadro negro:
 * - JSX y clases Tailwind IDÉNTICOS al original.
 * - NO cambia lógica de selección/branching, solo recibe los flags y handlers.
 * - Envuelve TODO en WorkspaceErrorBoundary igual que el original.
 */
const WorkspaceMainContent = ({
  // imagen / editor
  latestWorkspaceImage,
  showImageEditor,
  getVariantsForItem,
  onSelectVariant,
  onDeleteWorkspaceItem,
  onCloseImageEditor,
  onRefreshImages,
  onSelectFromGallery,
  // internal workspace
  forceInternalWorkspace,
  onInternalWorkspaceClosed,
  // empty / preview switches
  currentConversation,
  hasContent,
  hasExecutableCode,
  lastExecutableCodeBlock,
  hasPreviewableCode,
  jsxPreviewCode,
  hasImages,
  lastImage,
  onDownloadProject,
  onShowFiles,
  projectFiles = [],
}) => {
  const { language } = useLanguage();

  return (
    <div
      className="flex-1 overflow-hidden relative"
      data-testid="workspace-main-content"
      data-workspace-root="true"
      style={{
        backgroundColor: latestWorkspaceImage && showImageEditor ? '#f3f4f6' : '#1e293b',
      }}
    >
      <WorkspaceErrorBoundary language={language}>
        {/* PRIORIDAD 1: Editor de imágenes del workspace */}
        {latestWorkspaceImage && showImageEditor ? (
          <WorkspaceImageViewer
            workspaceItem={latestWorkspaceImage}
            variants={getVariantsForItem(
              latestWorkspaceImage.type === 'IMAGE_SOURCE'
                ? latestWorkspaceImage.id
                : latestWorkspaceImage.parent_id,
            )}
            onSelectVariant={onSelectVariant}
            onDeleteItem={onDeleteWorkspaceItem}
            onClose={onCloseImageEditor}
            onRefresh={onRefreshImages}
            onSelectFromGallery={onSelectFromGallery}
            language={language}
          />
        ) : forceInternalWorkspace ? (
          /* PRIORIDAD: Internal Workspace cuando se solicita (deep-link
             /workspace/internal o evento damyxia-open-internal-module).
             Va ANTES de EmptyWorkspaceMatrix para que el flag tenga prioridad
             incluso cuando no hay conversacion activa o contenido aun. */
          <InternalWorkspace
            onNavigateToChat={() => {
              if (onInternalWorkspaceClosed) onInternalWorkspaceClosed();
            }}
          />
        ) : !currentConversation || !hasContent ? (
          <EmptyWorkspaceMatrix showAvatar={!!currentConversation} />
        ) : (
          <div className="h-full flex flex-col bg-zinc-900">
            {hasExecutableCode && lastExecutableCodeBlock ? (
              <div className="flex-1 overflow-hidden border-0 bg-zinc-900">
                <ReactPreview
                  code={lastExecutableCodeBlock.code}
                  autoRun={true}
                  title=""
                  onDownloadProject={onDownloadProject}
                  onShowFiles={onShowFiles}
                  projectFiles={projectFiles}
                  conversationId={currentConversation?.id || currentConversation?._id || null}
                />
              </div>
            ) : hasPreviewableCode ? (
              <div className="flex-1 overflow-hidden border-0 bg-zinc-900/80">
                <UnifiedCodePreview
                  code={jsxPreviewCode}
                  title=""
                  height="100%"
                  showWatermark={false}
                  showControls={false}
                  theme="dark"
                />
              </div>
            ) : hasImages && lastImage ? (
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="relative rounded-xl overflow-hidden max-w-full max-h-full">
                  <img
                    src={lastImage.url}
                    alt={lastImage.alt || 'Imagen generada por DamyxIA'}
                    className="max-w-full max-h-[80vh] object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div class="flex items-center justify-center h-48 text-zinc-500">
                          <span>${language === 'es' ? 'Error al cargar imagen' : 'Failed to load image'}</span>
                        </div>
                      `;
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        )}
      </WorkspaceErrorBoundary>
    </div>
  );
};

export default WorkspaceMainContent;
